import { Injectable } from '@angular/core'
import { HttpClient, HttpStatusCode } from '@angular/common/http'
import { Observable, share } from 'rxjs'
import { AcceptApplicationOffer, CancelApplication, LoanApplication, PreliminaryApplication, SubmitApplication } from './loan-application.model'
import { ErrorsMap, nullOnHttpNotFound, remapErrors } from '@sun-finance/utils'
import { MarketingMetricsStoreService } from '@features/marketing-metrics-store/marketing-metrics-store.service'
import { createProgressStepHttpContext } from '@features/progress/util'
import { ACTION_STEP_NAME } from '@features/progress/contracts'

const submissionErrorsMap: ErrorsMap = {
  [HttpStatusCode.Conflict]: 'loan_application_conflict',
  [HttpStatusCode.TooManyRequests]: 'loan_application_too_many_requests',
}

const ROOT_PATH = '/auth/application/applications'
const LOAN_PATH = `${ROOT_PATH}/loan`
const PRELIMINARY_PATH = `${ROOT_PATH}/preliminary`

@Injectable({
  providedIn: 'root'
})
export class LoanApplicationService {

  all$ = this.http.get<LoanApplication[]>(LOAN_PATH, { params: { perPage: '100', offer: '1' } }).pipe(share())

  latest$: Observable<LoanApplication | null> = this.http.get<LoanApplication|null>(`${LOAN_PATH}/current`).pipe(nullOnHttpNotFound(), share())

  preliminary$: Observable<PreliminaryApplication | null> = this.http.get<PreliminaryApplication|null>(`${PRELIMINARY_PATH}/current`).pipe(nullOnHttpNotFound(), share())

  constructor(private http: HttpClient, private metricsStore: MarketingMetricsStoreService) { }

  submit(command: SubmitApplication): Observable<LoanApplication> {
    const marketingMetrics = this.metricsStore.retrieve()
    const context = createProgressStepHttpContext({
      stepName: ACTION_STEP_NAME.APPLICATION_CREATED,
      withMetadata: (application: LoanApplication) => ({ applicationId: application.id })
    })

    return this.http.post<LoanApplication>(LOAN_PATH, { ...command, marketingMetrics }, { context })
      .pipe(remapErrors(submissionErrorsMap))
  }

  cancel({ id, reason }: CancelApplication): Observable<void> {
    const context = createProgressStepHttpContext({
      stepName: ACTION_STEP_NAME.APPLICATION_CANCELLED,
      withMetadata: () => ({ applicationId: id })
    })

    return this.http.put<void>(
      `${LOAN_PATH}/${id}/cancellation`,
      {
        reason: { text: reason }
      },
      { context }
    )
  }

  provideBankStatement(id: string) {
    const context = createProgressStepHttpContext({
      stepName: ACTION_STEP_NAME.APPLICATION_BANK_STATEMENT_PROVIDED,
      withMetadata: () => ({ applicationId: id })
    })

    return this.http.put<void>(`${LOAN_PATH}/${id}/bank-statement-provide`, null, { context })
  }

  bankStatementDocumentsUpload(id: string, documents: {documentId: string}[]) {
    const context = createProgressStepHttpContext({
      stepName: ACTION_STEP_NAME.APPLICATION_BANK_STATEMENT_MANUAL_UPLOAD,
      withMetadata: () => ({ applicationId: id })
    })

    return this.http.put<void>(`${LOAN_PATH}/${id}/bank-statement-manual-upload`, { payload: documents }, { context })
  }

  acceptOffer({ id, variation }: AcceptApplicationOffer): Observable<void> {
    const context = createProgressStepHttpContext({
      stepName: ACTION_STEP_NAME.APPLICATION_OFFER_ACCEPTED,
      withMetadata: () => ({ applicationId: id, ...variation })
    })

    return this.http.put<void>(`${LOAN_PATH}/${id}/accept-offer`, { payload: variation }, { context })
  }

  convertPreliminary(id: string, purpose: string, conditions: { loanAmount: string, term: number }): Observable<PreliminaryApplication> {
    const marketingMetrics = this.metricsStore.retrieve()
    const context = createProgressStepHttpContext({
      stepName: ACTION_STEP_NAME.PRELIMINARY_APPLICATION_CONVERTED,
      withMetadata: () => ({ preliminaryApplicationId: id, })
    })

    return this.http.put<PreliminaryApplication>(`${PRELIMINARY_PATH}/${id}/convert`, { marketingMetrics, purpose, conditions }, { context })
      .pipe(remapErrors(submissionErrorsMap))
  }

  skipBankStatementRequest(id: string): Observable<void> {
    const context = createProgressStepHttpContext({
      stepName: ACTION_STEP_NAME.SKIP_BANK_STATEMENT_REQUEST,
      withMetadata: () => ({ applicationId: id, })
    })

    return this.http.put<void>(`${LOAN_PATH}/${id}/skip-bank-statement-request`, null, { context })
      .pipe(remapErrors(submissionErrorsMap))
  }

  skipManualBankStatementRequest(id: string): Observable<void> {
    const context = createProgressStepHttpContext({
      stepName: ACTION_STEP_NAME.SKIP_MANUAL_BANK_STATEMENT_REQUEST,
      withMetadata: () => ({ applicationId: id, })
    })

    return this.http.put<void>(`${LOAN_PATH}/${id}/skip-manual-bank-statement-request`, null, { context })
      .pipe(remapErrors(submissionErrorsMap))
  }
}
